import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';

import { registerUser } from '../../services/authService';
import '../Login/Login.css';
import './RegisterPage.css';

const validateForm = ({ email, password, confirmPassword }) => {
  const errors = {};
  const normalizedEmail = email.trim();

  if (!normalizedEmail) {
    errors.email = 'E-mail is required.';
  } else if (!isEmail(normalizedEmail)) {
    errors.email = 'Enter a valid e-mail address.';
  }

  if (!password) {
    errors.password = 'Password is required.';
  } else if (password.length < 12) {
    errors.password = 'Password must be at least 12 characters.';
  }

  if (!confirmPassword) {
    errors.confirmPassword = 'Confirm password is required.';
  } else if (password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }

  return errors;
};

const SUCCESS_STATES = {
  created: {
    title: 'Check your email',
    text: 'Your account has been created. We sent you a verification email.',
  },
  resent: {
    title: 'Verification email sent',
    text: 'A new verification link has been sent to your email address.',
  },
};

const CRITERION_CONTENT = {
  neutral: {
    icon: '\u25CB',
    screenReaderText: 'Not checked',
  },
  valid: {
    icon: '\u2713',
    screenReaderText: 'Requirement met',
  },
  invalid: {
    icon: '\u2715',
    screenReaderText: 'Requirement not met',
  },
};

const PasswordCriterion = ({ id, state, children }) => {
  const content = CRITERION_CONTENT[state];

  return (
    <p
      className={'registerCriterion registerCriterion--' + state}
      id={id}
      aria-live="polite"
    >
      <span className="registerCriterionIcon" aria-hidden="true">
        {content.icon}
      </span>
      <span>{children}</span>
      <span className="registerScreenReaderText">
        {' '}
        - {content.screenReaderText}
      </span>
    </p>
  );
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [requestError, setRequestError] = useState('');
  const [successState, setSuccessState] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passwordCriterionState = password
    ? password.length >= 12
      ? 'valid'
      : 'invalid'
    : 'neutral';
  const confirmPasswordCriterionState = confirmPassword
    ? password && password === confirmPassword
      ? 'valid'
      : 'invalid'
    : 'neutral';

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const errors = validateForm({ email, password, confirmPassword });
    setFieldErrors(errors);
    setRequestError('');

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await registerUser({
        email: email.trim(),
        password,
      });

      setSuccessState(response.status === 201 ? 'created' : 'resent');
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === 'EMAIL_VERIFICATION_PENDING') {
        setRequestError(
          'This account is waiting for email verification. Check your inbox. A new email was not sent.'
        );
      } else if (!status || status >= 500) {
        setRequestError(
          'Unable to create the account. Please try again later.'
        );
      } else {
        setRequestError(
          error?.response?.data?.message || 'Unable to create the account.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (successState) {
    const content = SUCCESS_STATES[successState];

    return (
      <main className="loginPage">
        <section
          className="loginCard registerSuccessCard"
          aria-labelledby="register-success-title"
        >
          <h1 id="register-success-title">{content.title}</h1>
          <p>{content.text}</p>
          <p>The verification link expires in 24 hours.</p>
          <button
            className="loginSubmitButton registerSignInButton"
            type="button"
            onClick={() => navigate('/login')}
          >
            Go to Sign In
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="loginPage">
      <section className="loginCard" aria-labelledby="register-title">
        <h1 id="register-title">Create account</h1>

        <form
          className="loginForm"
          onSubmit={handleSubmit}
          aria-busy={isSubmitting}
          noValidate
        >
          <div className="loginField">
            <label htmlFor="register-email">E-mail</label>
            <input
              id="register-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="name@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-describedby={fieldErrors.email ? 'register-email-error' : undefined}
              aria-invalid={Boolean(fieldErrors.email)}
              disabled={isSubmitting}
            />
            {fieldErrors.email && (
              <p className="registerFieldError" id="register-email-error">
                {fieldErrors.email}
              </p>
            )}
          </div>

          <div className="loginField">
            <label htmlFor="register-password">Password</label>
            <div className="loginPasswordControl">
              <input
                id="register-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-describedby={
                  fieldErrors.password
                    ? 'register-password-criterion register-password-error'
                    : 'register-password-criterion'
                }
                aria-invalid={Boolean(fieldErrors.password)}
                disabled={isSubmitting}
              />
              <button
                className="loginPasswordToggle"
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                disabled={isSubmitting}
                onClick={() => setShowPassword((currentValue) => !currentValue)}
              >
                <span aria-hidden="true">{showPassword ? '\u25C9' : '\u25CB'}</span>
              </button>
            </div>
            <PasswordCriterion
              id="register-password-criterion"
              state={passwordCriterionState}
            >
              At least 12 characters
            </PasswordCriterion>
            {fieldErrors.password && (
              <p className="registerFieldError" id="register-password-error">
                {fieldErrors.password}
              </p>
            )}
          </div>

          <div className="loginField">
            <label htmlFor="register-confirm-password">Confirm password</label>
            <div className="loginPasswordControl">
              <input
                id="register-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                aria-describedby={
                  fieldErrors.confirmPassword
                    ? 'register-confirm-password-criterion register-confirm-password-error'
                    : 'register-confirm-password-criterion'
                }
                aria-invalid={Boolean(fieldErrors.confirmPassword)}
                disabled={isSubmitting}
              />
              <button
                className="loginPasswordToggle"
                type="button"
                aria-label={
                  showConfirmPassword ? 'Hide password' : 'Show password'
                }
                aria-pressed={showConfirmPassword}
                disabled={isSubmitting}
                onClick={() =>
                  setShowConfirmPassword((currentValue) => !currentValue)
                }
              >
                <span aria-hidden="true">
                  {showConfirmPassword ? '\u25C9' : '\u25CB'}
                </span>
              </button>
            </div>
            <PasswordCriterion
              id="register-confirm-password-criterion"
              state={confirmPasswordCriterionState}
            >
              Passwords match
            </PasswordCriterion>
            {fieldErrors.confirmPassword && (
              <p
                className="registerFieldError"
                id="register-confirm-password-error"
              >
                {fieldErrors.confirmPassword}
              </p>
            )}
          </div>

          {requestError && (
            <p className="loginError" role="alert">
              {requestError}
            </p>
          )}

          <button
            className="loginSubmitButton"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <p className="loginRegisterText">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </section>
    </main>
  );
};

export default RegisterPage;
